import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../../utils/api';
import config from '../../../config';
import i18n from '../../../i18n';
import {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchUnits,
  fetchUnitsSuccess,
  fetchUnitsFailure,
  fetchUnitById,
  fetchUnitByIdSuccess,
  fetchUnitByIdFailure,
  addUnit,
  addUnitSuccess,
  addUnitFailure,
  updateUnit,
  updateUnitSuccess,
  updateUnitFailure,
  updateUnitStatus,
  updateUnitStatusSuccess,
  updateUnitStatusFailure,
  deleteUnit,
  deleteUnitSuccess,
  deleteUnitFailure,
  bulkDeleteUnits,
  bulkDeleteUnitsSuccess,
  bulkDeleteUnitsFailure,
} from './slice';

/**
 * Extract error message from various backend error formats
 */
function extractErrorMessage(error) {
  if (!error) return null;

  if (typeof error === 'string') return error;

  if (error.message) return error.message;

  if (error.errors) {
    if (typeof error.errors === 'object' && !Array.isArray(error.errors)) {
      const firstKey = Object.keys(error.errors)[0];
      if (firstKey && Array.isArray(error.errors[firstKey])) {
        return error.errors[firstKey][0];
      }
    }
    if (Array.isArray(error.errors) && error.errors.length > 0) {
      return error.errors[0];
    }
  }

  return null;
}

/**
 * Translate error message from backend
 */
function translateError(error) {
  const errorMessage = extractErrorMessage(error);

  if (!errorMessage) return i18n.t('licenseUnits.errors.unknown');

  const errorMap = {
    'unit code already exists': 'licenseUnits.errors.codeExists',
    'code already exists': 'licenseUnits.errors.codeExists',
    'mã đơn vị đã tồn tại': 'licenseUnits.errors.codeExists',
    'unit name already exists': 'licenseUnits.errors.nameExists',
    'name already exists': 'licenseUnits.errors.nameExists',
    'tên đơn vị đã tồn tại': 'licenseUnits.errors.nameExists',
    'unit not found': 'licenseUnits.errors.unitNotFound',
    'not found': 'licenseUnits.errors.unitNotFound',
  };

  const lowerMessage = errorMessage.toLowerCase();

  for (const [key, translationKey] of Object.entries(errorMap)) {
    if (lowerMessage.includes(key)) {
      return i18n.t(translationKey);
    }
  }

  return errorMessage;
}

// Worker saga: Fetch init data
function* handleFetchInit() {
  try {
    const response = yield call(api.get, config.endpoints.units.init);
    yield put(fetchInitSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(fetchInitFailure(translatedError));
  }
}

// Worker saga: Fetch units
function* handleFetchUnits(action) {
  try {
    const { page, pageSize, sortColumn, sortDirection, filterName, filterLevel, filterStatus, filterLicense } = action.payload || {};
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      params.sortColumn = sortColumn;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterName) {
      params.name = filterName;
    }
    if (filterLevel !== undefined && filterLevel !== '') {
      params.level = filterLevel;
    }
    if (filterStatus !== undefined && filterStatus !== '') {
      params.status = filterStatus;
    }
    if (filterLicense) {
      params.licenseCode = filterLicense;
    }

    const response = yield call(api.get, config.endpoints.units.list, params);
    yield put(fetchUnitsSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(fetchUnitsFailure(translatedError));
  }
}

// Worker saga: Fetch unit by ID
function* handleFetchUnitById(action) {
  try {
    const unitId = action.payload;
    const response = yield call(api.get, `${config.endpoints.units.list}/${unitId}`);
    yield put(fetchUnitByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(fetchUnitByIdFailure(translatedError));
  }
}

// Worker saga: Add unit
function* handleAddUnit(action) {
  try {
    const response = yield call(api.post, config.endpoints.units.create, action.payload);
    yield put(addUnitSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(addUnitFailure(translatedError));
  }
}

// Worker saga: Update unit
function* handleUpdateUnit(action) {
  try {
    const { id } = action.payload;
    const response = yield call(api.put, `${config.endpoints.units.update}/${id}`, action.payload);
    yield put(updateUnitSuccess(response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(updateUnitFailure(translatedError));
  }
}

// Worker saga: Update unit status
function* handleUpdateUnitStatus(action) {
  try {
    const { id, status } = action.payload;
    const response = yield call(api.put, `${config.endpoints.units.list}/${id}/status`, { status });
    yield put(updateUnitStatusSuccess(response.data || response));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(updateUnitStatusFailure(translatedError));
  }
}

// Worker saga: Delete unit
function* handleDeleteUnit(action) {
  try {
    const unitId = action.payload;
    yield call(api.delete, `${config.endpoints.units.delete}/${unitId}`);
    yield put(deleteUnitSuccess(unitId));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(deleteUnitFailure(translatedError));
  }
}

// Worker saga: Bulk delete units
function* handleBulkDeleteUnits(action) {
  try {
    const unitIds = action.payload;
    yield call(api.delete, config.endpoints.units.delete, {
      params: { ids: unitIds },
      paramsSerializer: {
        indexes: null,
      },
    });
    yield put(bulkDeleteUnitsSuccess(unitIds));
  } catch (error) {
    const translatedError = translateError(error);
    yield put(bulkDeleteUnitsFailure(translatedError));
  }
}

// Watcher saga
export function* watchLicenseUnitsActions() {
  yield takeLatest(fetchInit.type, handleFetchInit);
  yield takeLatest(fetchUnits.type, handleFetchUnits);
  yield takeLatest(fetchUnitById.type, handleFetchUnitById);
  yield takeLatest(addUnit.type, handleAddUnit);
  yield takeLatest(updateUnit.type, handleUpdateUnit);
  yield takeLatest(updateUnitStatus.type, handleUpdateUnitStatus);
  yield takeLatest(deleteUnit.type, handleDeleteUnit);
  yield takeLatest(bulkDeleteUnits.type, handleBulkDeleteUnits);
}
